import { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Timeline,
  Tag,
  Empty,
  Image,
  Space
} from 'antd';
import { useTranslation } from 'react-i18next';
import { GENDERS_VALUES } from 'constants/users';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import {
  MEDICAL_TREATMENT_COLOR,
  MEDICAL_TREATMENT_LABEL,
  MEDICAL_TREATMENT_STATUS,
  MedicalTreatmentValues
} from 'constants/appointments';
import { useUIDispatchContext } from 'providers/UI';
import { WAY_OF_COMMUNICATION } from 'constants/medical_treatment';
import { parseJson } from '@medicusunion/front-utils';
import { useOrganizationsStateContext } from 'providers/Organizations';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { usePermission } from 'hooks';
import {
  fetchTreatmentFormById,
  updateTreatmentForm
} from 'services/api/treatmentForm';
import { TreatmentFormDetail } from 'types/api/treatmentForm';

import useOrgEffect from 'hooks/useOrgEffect';
import { getFileUrl } from 'utils/file';
import { Loading, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';

const CALCULATION_LABELS = {
  documentTranslation: 'Document Translation',
  visaAssistance: 'Visa Assistance',
  airportTransfer: 'Airport Transfer',
  interpreterServices: 'Interpreter Services',
  fullClinicSupport: 'Full Clinic Support',
  organization: 'Country',
  vipService: 'Vip Service'
};

const MedicalTreatmentDetails: FC = () => {
  const { toggleModal } = useUIDispatchContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const { checkUserPermission } = usePermission();

  const { results: organizations } = useOrganizationsStateContext();

  const [medicalTreatment, setMedicalTreatment] =
    useState<TreatmentFormDetail | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useOrgEffect(() => {
    if (!params.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchTreatmentFormById(params.id as string);
        if (!cancelled) setMedicalTreatment(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id, refreshKey]);

  const onStatusSubmit = async (values: { note: string; status: string }) => {
    if (!params.id) return;

    try {
      await updateTreatmentForm(params.id, {
        status: String(values.status),
        note: values.note
      });
      toggleModal('updateStatus', false);
      refetch();
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const medicalTreatmentId = medicalTreatment;
  const files = medicalTreatmentId?.files || [];

  const calculations = Object.entries(
    parseJson(medicalTreatmentId?.calculations) || {}
  );

  const renderCalculationValue = (key: string, value: unknown) => {
    switch (key) {
      case 'airportTransfer':
      case 'documentTranslation':
      case 'fullClinicSupport':
      case 'vipService':
      case 'visaAssistance':
        return value ? 'YES' : 'NO';

      case 'interpreterServices':
        switch (value) {
          case 'interpreterServicesValuesUp2hours':
            return 'up 2 hours';
          case 'interpreterServicesValuesFrom2to4hours':
            return '2-4 hours';
          case 'interpreterServicesValuesFrom4to6hours':
            return '4-6 hours';
          case 'interpreterServicesValuesFullDay':
            return 'Full day';
          default:
            return '-';
        }

      case 'organization': {
        if (value) {
          const organization = organizations.find(item => item.value === value);

          if (organization) {
            return (
              <Space size={8}>
                <Image
                  src={getFileUrl(organization.icon)}
                  width={24}
                  height={24}
                  preview={false}
                />
                {organization.label}
              </Space>
            );
          }
        }

        return '-';
      }

      default:
        return '-';
    }
  };

  const hasFileViewPermission = checkUserPermission(
    PERMISSION_ROUTES.medicalTreatment,
    PERMISSION_ACTIONS.viewFile
  );

  if (!medicalTreatmentId || loading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      onClickBack={() => navigate(-1)}
      title={`Medical Treatment: #${params.id}`}
      extra={[
        <Private
          key="change-status-button"
          route={PERMISSION_ROUTES.medicalTreatment}
          action={PERMISSION_ACTIONS.update}
        >
          <Button
            type="primary"
            onClick={() =>
              toggleModal('updateStatus', true, {
                id: params.id,
                currentStatus: `${medicalTreatmentId.status}`,
                statusList: MEDICAL_TREATMENT_LABEL,
                onSubmit: onStatusSubmit
              })
            }
          >
            Change status
          </Button>
        </Private>
      ]}
    >
      <Card title="Personal Information" style={{ marginBottom: 24 }}>
        <Descriptions labelStyle={{ fontWeight: 'bold' }}>
          <Descriptions.Item label="User Name">
            {medicalTreatmentId.first_name} {medicalTreatmentId.last_name}
          </Descriptions.Item>
          <Descriptions.Item label="Phone number">
            <a href={`tel:${medicalTreatmentId.phone}`}>
              {medicalTreatmentId.phone}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <a href={`mailto:${medicalTreatmentId.email}`}>
              {medicalTreatmentId.email}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Gender">
            {!!medicalTreatmentId.gender && !medicalTreatmentId.calculations
              ? t(
                  `appointments_details.${
                    GENDERS_VALUES[
                      medicalTreatmentId.gender as keyof typeof GENDERS_VALUES
                    ]
                  }`
                )
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Date of birth">
            {medicalTreatmentId.date_of_birth
              ? dayjs(medicalTreatmentId.date_of_birth).format(DATE_FORMAT)
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Created at">
            {dayjs(medicalTreatmentId.created_at).format(FULL_DATE_FORMAT)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Form Details" style={{ marginBottom: 24 }}>
        <Descriptions
          layout="vertical"
          column={1}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Preferred Way of Communication">
            {medicalTreatmentId.way_of_communication
              ? WAY_OF_COMMUNICATION.find(
                  item =>
                    item.value ===
                    (medicalTreatmentId.way_of_communication as unknown as typeof item.value)
                )?.label
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {medicalTreatmentId.description
              ? medicalTreatmentId.description
              : '-'}
          </Descriptions.Item>
          {hasFileViewPermission && (
            <Private
              route={PERMISSION_ROUTES.medicalTreatment}
              action={PERMISSION_ACTIONS.viewFile}
            >
              <Descriptions.Item label="Files (Deprecated)" span={2}>
                {files.length ? (
                  <ul>
                    {files.map(item => {
                      return (
                        <li key={`file-${item.filename}-${item.id}`}>
                          <a
                            href={getFileUrl(item.filename)}
                            download={item}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {item.filename}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Private>
          )}
        </Descriptions>
      </Card>
      <Card title="Calculation Options" style={{ marginBottom: 24 }}>
        {calculations.length ? (
          <Descriptions labelStyle={{ fontWeight: 'bold' }}>
            {calculations.map(([key, value]) => (
              <Descriptions.Item
                label={
                  CALCULATION_LABELS[key as keyof typeof CALCULATION_LABELS] ||
                  key
                }
                key={key}
              >
                <>{renderCalculationValue(key, value)}</>
              </Descriptions.Item>
            ))}
          </Descriptions>
        ) : (
          <Empty description="There are no calculation options for this request" />
        )}
      </Card>
      <Card title="History">
        <Timeline
          items={[
            ...(Array.isArray(medicalTreatmentId.history)
              ? medicalTreatmentId.history.map(item => ({
                  children: (
                    <>
                      <p key={`history-${item.id}`}>
                        Status changed to{' '}
                        <Tag
                          color={
                            MEDICAL_TREATMENT_COLOR[
                              item.status as MedicalTreatmentValues
                            ]
                          }
                        >
                          {
                            MEDICAL_TREATMENT_LABEL[
                              item.status as MedicalTreatmentValues
                            ]
                          }
                        </Tag>
                        on{' '}
                        <b>{dayjs(item.created_at).format(FULL_DATE_FORMAT)}</b>
                      </p>
                      <p style={{ fontWeight: 500 }}>{item.note}</p>
                    </>
                  )
                }))
              : []),
            {
              children: (
                <p>
                  Request created with a status{' '}
                  <Tag
                    color={
                      MEDICAL_TREATMENT_COLOR[MEDICAL_TREATMENT_STATUS.new]
                    }
                  >
                    {MEDICAL_TREATMENT_LABEL[MEDICAL_TREATMENT_STATUS.new]}
                  </Tag>
                  on{' '}
                  <b>
                    {dayjs(medicalTreatmentId.created_at).format(
                      FULL_DATE_FORMAT
                    )}
                  </b>
                </p>
              )
            }
          ]}
        />
      </Card>
    </PageWrapper>
  );
};

export default MedicalTreatmentDetails;
